import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Timer } from '../../components/general/Timer';
import { usePostApi } from '../../hooks/post_api_hook/usePostApi';
import { payloadCopie } from '../../utils/copieQuiz';
import { correctionSurPlace } from '../../utils/rejeuQuiz';
import { url } from "../../utils/confserveur";
import Loading from '../../components/general/Loading';
import { useClasseContext } from '../../context/ClasseContext';
import TableResponseElementEleve from '../../components/VueEvalNat/BilanEval/FormBilanEval/FormQuestion/Elements/TableResponseElementEleve';
import RichTextBlockEleve from '../../components/VueQuestion/RichTextBlockEleve';
import { BookOpen, X, ChevronLeft, ChevronRight, Loader2, AlertTriangle, Volume2, VolumeX, Bell, BellOff, WifiOff, RefreshCw } from 'lucide-react';
import QuestionInteractive from '../../components/VueQuestion/interactifs/QuestionInteractive';
import ZoomableMedia from '../../components/VueQuestion/ZoomableMedia';
import { QuizThemeContext } from '../../components/VueQuestion/interactifs/quizTheme';
import { compareAnswers, compareComplexAnswers, isComplexAnswer, parseComplexAnswer, buildPreviewContenu, toleranceNumerique, comparaisonExpression, comparaisonDe, reponsesAcceptees, trousInterchangeables, optionsDeFiche, calculatriceAutorisee, calculatriceCosinus, resolveBonneReponse, trouverErreurs, enonceDeQuestion, annoterReponses } from '../../utils/questionUtils';
import { useXp } from '../../context/XpContext';
import { XP_BONNE, XP_MAUVAIS, XP_BONNE_AIDE, XP_BONNE_AUTO, XP_BONNE_AIDE_AUTO, XP_EFFORT_AUTO } from '../../utils/xpConfig';
import { marquerDefiFait } from '../../utils/defisQuotidiens';
import { playAbandon, isSfxMuted, setSfxMuted } from '../../utils/xpSound';
import { pickMessage, pickStreakMessage, pickTip } from '../../utils/xpMessages';
import { isCoupDePouceEnabled, getIndice } from '../../utils/coupDePouce';
import XpAnswerFeedback from './XpAnswerFeedback';
import QuizProgress from './QuizProgress';
import StreakBadge from './StreakBadge';
import CoupDePouceButton from './CoupDePouceButton';
import CalculatriceCollege from './CalculatriceCollege';
import { supportDeQuestion } from '../../generators/evalnat/supports';
import { libellePartie, partieAvecTexte } from './partiesQuiz';
import LivreImmersif from './LivreImmersif';

// Construire la config TableResponseElementEleve depuis bonne_reponse au format [phrase|valeur]
const buildTableConfig = (answer) => {
  const parsed = parseComplexAnswer(answer);
  if (!parsed) return null;

  const lignes = parsed.map(item => item.phrase);
  const uniqueReponses = [...new Set(parsed.map(item => item.reponse))];
  const isVraiFaux = uniqueReponses.every(r => ['vrai', 'faux'].includes(r.toLowerCase()));
  const colonnes = isVraiFaux ? ['Vrai', 'Faux'] : uniqueReponses;

  const bonnesReponses = parsed.map(item => {
    if (isVraiFaux) return item.reponse.toLowerCase() === 'vrai' ? 0 : 1;
    return colonnes.indexOf(item.reponse);
  });

  const allReponses = parsed.map(item => item.reponse);
  const hasDuplicateReponses = allReponses.length !== new Set(allReponses).size;

  let tableType = 'association';
  if (isVraiFaux) tableType = 'vraiFaux';
  else if (hasDuplicateReponses) tableType = 'colonnesLibres';

  return { type: tableType, titre: '', sousTitre: '', colonnes, lignes, bonnesReponses };
};

// Fonction pour extraire la configuration de tableau depuis elements_additionnels
// OU la générer automatiquement depuis bonne_reponse si format [phrase|valeur]
const getTableElementConfig = (question) => {
  // 1. D'abord essayer depuis elements_additionnels
  if (question?.elements_additionnels) {
    try {
      const data = typeof question.elements_additionnels === 'string'
        ? JSON.parse(question.elements_additionnels)
        : question.elements_additionnels;

      if (data?.elements) {
        const tableElement = data.elements.find(el => el.type === 'table');
        if (tableElement?.config) {
          return tableElement.config;
        }
      }
    } catch (e) {
      console.error('Erreur parsing elements_additionnels:', e);
    }
  }

  // 2. Sinon, essayer de générer la config depuis bonne_reponse
  if (question?.bonne_reponse && isComplexAnswer(question.bonne_reponse)) {
    return buildTableConfig(question.bonne_reponse);
  }

  return null;
};


// Importez vos données de questions ici, ainsi que les images si nécessaire
export const Quiz = ({questions, setLaunch, evalBilan, eleveId, classe_id}) => {
   const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showTextModal, setShowTextModal] = useState(false);

const {classeNom} = useClasseContext()

   // On détermine dynamiquement la première catégorie non vide [type_label]
  const getFirstNonEmptyCategory = () => {
    const categories = Object.keys(questions);
    for (let cat of categories) {
      if (questions[cat] && Object.keys(questions[cat]).length > 0) {
        return cat;
      }
    }
    return categories[0]; // Par défaut, première catégorie
  };
  
  const [currentPart, setCurrentPart] = useState(getFirstNonEmptyCategory) 
  const [categoriesOrder, setCategoriesOrder] = useState([]);

  // Endpoint de soumission selon la source (bilan EvalNat, session en ligne
  // ou défi quotidien généré côté client)
  const isSession = evalBilan?.source === 'session';
  const isDefi = evalBilan?.source === 'defi';
  const submitUrl = isDefi
    ? `${url}defis-quotidiens/submit/`
    : isSession
      ? `${url}eleves_sessions_qcm/submit_responses/`
      : `${url}eleves_evaluations_nat/update_reponses/`;
  const [, doPost] = usePostApi(submitUrl);

  // ─── Remise de la copie ───
  // `isLoading` / `isError` du hook ne sont PAS lus ici : ce sont des états
  // React, lisibles au rendu suivant seulement. La closure qui vient d'appeler
  // `doPost` y verrait la valeur du rendu précédent. Le verdict qui fait foi est
  // l'objet `{ success }` renvoyé par `doPost`.
  const [envoiEchoue, setEnvoiEchoue] = useState(false);
  const [envoiEnCours, setEnvoiEnCours] = useState(false);
  const copieEnAttenteRef = useRef(null); // {results, apresSucces} d'un envoi à rejouer

  const [hasAnswered, setHasAnswered] = useState(false);
  const { refreshProfil } = useXp();
  // Mode correction visible : révélation du feedback Duolingo après chaque réponse
  const [revealed, setRevealed] = useState(false);
  const [sfxMuted, setSfxMutedState] = useState(isSfxMuted());
  const toggleSfx = () => { const m = !sfxMuted; setSfxMuted(m); setSfxMutedState(m); };

  // ─── Rétention (cosmétique, aucun impact scoring) ───
  // Série de bonnes réponses consécutives (mode correction visible uniquement).
  const [streak, setStreak] = useState(0);
  const bestStreakRef = useRef(0);
  const PALIERS_STREAK = [3, 5, 10];
  // Messages du feedback (tirés au clic « Vérifier » → stables pendant le sheet).
  const [feedbackMsg, setFeedbackMsg] = useState('');
  const [streakMsg, setStreakMsg] = useState(null);
  const [streakPalier, setStreakPalier] = useState(0);
  // Direction de la transition entre questions (slide latéral).
  const [slideDir, setSlideDir] = useState('next');
  // Coup de pouce (V1 UI seule, flag off par défaut — lu une fois au montage).
  const [cpEnabled] = useState(isCoupDePouceEnabled);
  const [aideOuverte, setAideOuverte] = useState(false);
  const [aidesUtilisees, setAidesUtilisees] = useState({});


  // Ajoutez un état pour gérer si le composant est monté
  const [isMounted, setIsMounted] = useState(false);

  // État pour le chargement des images
  const [imageLoading, setImageLoading] = useState(true);

  // État pour le modal de confirmation de sortie
  const [showExitWarning, setShowExitWarning] = useState(false);

  // État lecture audio (TTS navigateur) de la question courante
  const [isSpeaking, setIsSpeaking] = useState(false);
  // ─── Immersion Duolingo : toast éphémère de partie, flash de série.
  //     Zéro logique de réponse ici.
  const [partToast, setPartToast] = useState(null);
  const [streakFlash, setStreakFlash] = useState(false);
  // Toast éphémère au changement de partie (le nom de partie n'est plus
  // affiché en permanence — pattern Duolingo « nouvelle leçon »).
  const premierePartieRef = useRef(true);
  useEffect(() => {
    if (premierePartieRef.current) { premierePartieRef.current = false; return undefined; }
    setPartToast(currentPart);
    const tm = setTimeout(() => setPartToast(null), 1800);
    return () => clearTimeout(tm);
  }, [currentPart]);
  // Flash éphémère de la série (le badge permanent a quitté l'en-tête).
  useEffect(() => {
    if (streak < 2) return undefined;
    setStreakFlash(true);
    const tm = setTimeout(() => setStreakFlash(false), 1600);
    return () => clearTimeout(tm);
  }, [streak]);

  // Ref pour garder une référence aux réponses actuelles (pour beforeunload)
  const answersRef = useRef(answers);
  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  // Verrou anti-double-envoi : levé dès qu'un envoi de FIN démarre
  // (postReponses / handleConfirmExit). Le beacon de fermeture d'onglet ne
  // sert qu'à rattraper une sortie EN COURS de quiz — sans ce verrou, fermer
  // l'onglet pendant l'écran « Chargement… » post-fin doublait la soumission
  // (2 POST + double marquerDefiFait sur un défi).
  const dejaEnvoyeRef = useRef(false);

  /**
   * Annote la copie d'un quiz en ligne avant l'envoi : `aide: true` là où le
   * coup de pouce a été pris (contrat back : flag INLINE par question ; clé
   * absente = pas d'aide, plein tarif) et `erreurs`, chaque code d'erreur
   * reconnu sur une réponse donnée avec le morceau de réponse qui l'a déclenché
   * (brief docs/BRIEF_LOT1_CODES_ERREUR.md).
   * Clone, ne mute jamais l'état. Appliquée aux TROIS chemins d'envoi
   * (postReponses, handleConfirmExit, sendBeacon de sortie).
   */
  const annoterCopie = useCallback(
    (reponsesObj) => annoterReponses(reponsesObj, questions, aidesUtilisees),
    [questions, aidesUtilisees],
  );

  /**
   * Payload de soumission d'un DÉFI QUOTIDIEN (contrat back du 12/07) :
   * liste plate de réponses avec provenance (attendu, generator_key) — le
   * back RECOMPTE nb_bonnes/nb_aides depuis `reponses` (anti-triche), on
   * n'envoie donc aucun agrégat. Scores posés par verifScore/compareAnswers
   * juste avant l'appel ; sans réponse → score 0 (barème effort).
   */
  const buildDefiPayload = useCallback((reponsesObj) => {
    const reponses = [];
    Object.keys(questions).forEach((cat) => {
      Object.keys(questions[cat] || {}).forEach((qKey) => {
        const q = questions[cat][qKey];
        const r = reponsesObj?.[cat]?.[qKey] || {};
        // L'énoncé, pour que le professeur lise la réponse fausse (absent
        // quand la question porte une figure), et les erreurs reconnues, chaque
        // code avec son morceau de réponse (brief docs/BRIEF_LOT1_CODES_ERREUR.md).
        const enonce = enonceDeQuestion(q);
        reponses.push({
          attendu: q.attendu ?? null,
          generator_key: q.generator_key ?? null,
          question_num: q.question_num ?? reponses.length + 1,
          ...(enonce ? { enonce } : {}),
          reponse: r.réponse ?? '',
          bonne_reponse: r.bonne_reponse ?? q.bonne_reponse ?? '',
          score: r.score === 1 ? 1 : 0,
          aide: !!aidesUtilisees[`${cat}::${qKey}`],
          erreurs: trouverErreurs(q, r),
        });
      });
    });
    return {
      eleve_id: eleveId,
      classe_id: parseInt(classe_id, 10),
      niveau: evalBilan?.niveau || null,
      matiere: evalBilan?.matiere || 'maths',
      theme: evalBilan?.theme || '',
      nb_questions: reponses.length,
      reponses,
    };
  }, [questions, aidesUtilisees, eleveId, classe_id, evalBilan?.niveau, evalBilan?.matiere, evalBilan?.theme]);

  /**
   * Dépose le bilan du quiz (bonnes/mauvaises/sans réponse/aides/meilleure
   * série) dans sessionStorage pour le récap de fin : celui-ci est déclenché
   * par XpContext APRÈS le démontage de Quiz — sans ce canal, les compteurs
   * locaux seraient perdus. Le récap lit puis efface la clé.
   */
  const stasherBilanQuiz = useCallback((reponsesObj) => {
    let bonnes = 0;
    let mauvaises = 0;
    let sansReponse = 0;
    Object.keys(reponsesObj).forEach((cat) => {
      Object.keys(reponsesObj[cat] || {}).forEach((qKey) => {
        const r = reponsesObj[cat][qKey];
        if (r.score === 1) bonnes += 1;
        else if (r.score === 0) mauvaises += 1;
        else sansReponse += 1;
      });
    });
    try {
      sessionStorage.setItem('tanoRecapQuiz', JSON.stringify({
        bonnes,
        mauvaises,
        sansReponse,
        aides: Object.keys(aidesUtilisees).length,
        meilleureSerie: bestStreakRef.current,
        // Rejeu d'un quiz en ligne : le premier essai (la copie officielle), mis en regard.
        ...(evalBilan?.premierEssai ? { premierEssai: evalBilan.premierEssai } : {}),
      }));
    } catch { /* no-op : le récap se contentera de l'XP */ }
  }, [aidesUtilisees, evalBilan?.premierEssai]);

  // Stoppe la lecture audio au changement de question / au démontage
  useEffect(() => {
    setIsSpeaking(false);
    return () => { if (typeof window !== 'undefined') window.speechSynthesis?.cancel(); };
  }, [currentPart, currentQuestionIndex]);



// Initialiser l'ordre des catégories et les réponses vides
  useEffect(() => {
    if (!questions) return;
    
    // On crée un objet pour stocker toutes les réponses initiales
    const initialAnswers = {};
    const categoriesWithQuestions = [];
    
    // On parcourt toutes les catégories disponibles
    Object.keys(questions).forEach(category => {
      if (questions[category] && Object.keys(questions[category]).length > 0) {
        // Cette catégorie a des questions, l'ajouter à la liste
        categoriesWithQuestions.push(category);
        
        // Initialiser les réponses vides pour cette catégorie
        initialAnswers[category] = {};
        Object.keys(questions[category]).forEach(questionKey => {
          initialAnswers[category][questionKey] = {};
        });
      }
    });
    
    setCategoriesOrder(categoriesWithQuestions);
    setAnswers(prevAnswers => {
    const updatedAnswers = { ...prevAnswers };
    
    categoriesWithQuestions.forEach(category => {
      // Si cette catégorie n'existe pas encore, l'initialiser
      if (!updatedAnswers[category]) {
        updatedAnswers[category] = {};
        Object.keys(questions[category]).forEach(questionKey => {
          updatedAnswers[category][questionKey] = {};
        });
      }
    });
    
    return updatedAnswers;
  });
    
    // S'assurer que currentPart est dans la liste
    if (categoriesWithQuestions.length > 0 && !categoriesWithQuestions.includes(currentPart)) {
      setCurrentPart(categoriesWithQuestions[0]);
    }
  }, [questions,currentPart]);


// Ce useEffect se déclenche chaque fois que la question actuelle, la partie actuelle ou les réponses changent.
// Remplacez votre useEffect problématique par celui-ci :
useEffect(() => {
  // Garde : currentPart peut être transitoirement invalide (questions vides au
  // montage, catégorie disparue après suppression de questions en banque).
  // L'effet d'init recale currentPart au render suivant — on repassera ici.
  // Même protection que handleNextQuestion (questions[currentPart] || {}).
  const partQuestions = questions?.[currentPart];
  if (!partQuestions) return undefined;
  const currentQuestionKey = Object.keys(partQuestions)[currentQuestionIndex];

  // Utiliser un setTimeout pour vérifier APRÈS que React ait fini ses mises à jour
  const timer = setTimeout(() => {
    if (answers[currentPart] && answers[currentPart][currentQuestionKey] &&  
        answers[currentPart][currentQuestionKey].réponse !== undefined &&
        answers[currentPart][currentQuestionKey].réponse !== "") {
      setHasAnswered(true);
    } else {
      setHasAnswered(false);
    }
  }, 0); // 0ms suffit pour passer au prochain cycle
  
  // Nettoyer le timer
  return () => clearTimeout(timer);
}, [currentQuestionIndex, currentPart, answers, questions]);

  useEffect(() => {
    setIsMounted(true);

  }, [currentQuestionIndex, answers]);

  // Fonction pour préparer les réponses avec les questions manquantes (réponses vides)
  const prepareAnswersWithEmptyResponses = useCallback(() => {
    const completeAnswers = { ...answersRef.current };

    // Parcourir toutes les catégories et questions pour s'assurer qu'elles ont une réponse (même vide)
    Object.keys(questions).forEach(category => {
      if (!completeAnswers[category]) {
        completeAnswers[category] = {};
      }

      Object.keys(questions[category]).forEach(questionKey => {
        const question = questions[category][questionKey];
        if (!completeAnswers[category][questionKey] || completeAnswers[category][questionKey].réponse === undefined) {
          // Ajouter une réponse vide pour cette question
          completeAnswers[category][questionKey] = {
            réponse: "",
            bonne_reponse: question.bonne_reponse,
            image: null,
            question_num: question.question_num,
            bonne_reponse_image: question.bonne_reponse_image || null,
            score: "" // Pas de score car pas de réponse
          };
        }
      });
    });

    return completeAnswers;
  }, [questions]);

  // Envoyer les réponses via sendBeacon quand l'utilisateur quitte la page
  const sendResponsesOnExit = useCallback(() => {
    if (dejaEnvoyeRef.current) return; // fin déjà soumise → ne pas doubler
    const completeAnswers = prepareAnswersWithEmptyResponses();

    // Calculer les scores pour les réponses existantes
    Object.keys(completeAnswers).forEach(category => {
      Object.keys(completeAnswers[category]).forEach(questionKey => {
        const answer = completeAnswers[category][questionKey];
        if (answer.réponse && answer.réponse !== "") {
          const studentResponse = answer.réponse;
          const correctResponse = answer.bonne_reponse;

          if (isComplexAnswer(studentResponse) && isComplexAnswer(correctResponse)) {
            answer.score = compareComplexAnswers(studentResponse, correctResponse) ? 1 : 0;
          } else {
            // Comparateur unifié — même juge que verifScore (JSON, construction
            // géométrique, normalisation accents/casse). L'ancienne égalité
            // stricte comptait faux des réponses justes (constructions, JSON).
            answer.score = compareAnswers(studentResponse, correctResponse, answer.bonne_reponse_image, optionsDeFiche(answer)) ? 1 : 0;
          }
        }
      });
    });

    // Sortie (onglet fermé, refresh) : la copie part, blancs compris, mais
    // `termine: false` : pas d'XP (décision prof 14/09, le back est juge).
    const results = payloadCopie({
      termine: false, isDefi, isSession, evaluationId: evalBilan.id, eleveId, classeId: classe_id,
      reponses: isDefi ? undefined : annoterCopie(completeAnswers),
      defi: isDefi ? buildDefiPayload(completeAnswers) : undefined,
      rejeuDe: evalBilan.rejeuDe,
    });

    // Utiliser sendBeacon pour envoyer les données même si la page se ferme
    const blob = new Blob([JSON.stringify(results)], { type: 'application/json' });
    navigator.sendBeacon(submitUrl, blob);
    // Pas de marquerDefiFait ici : un défi quitté n'entre pas dans les cinq
    // crédités du jour (décision prof 14/09), le compteur local ne bouge pas.
  }, [prepareAnswersWithEmptyResponses, annoterCopie, buildDefiPayload, evalBilan.id, evalBilan.rejeuDe, eleveId, classe_id, isSession, isDefi, submitUrl]);

  // Détecter quand l'utilisateur essaie de quitter la page
  useEffect(() => {
    // Warning avant de quitter (fermeture onglet, refresh, etc.)
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = 'Tu n\'as pas terminé le quiz ! Tes réponses seront envoyées si tu quittes, sans XP.';
      return e.returnValue;
    };

    // Envoyer les réponses quand la page se ferme
    const handlePageHide = () => {
      sendResponsesOnExit();
    };

    // Ajouter les listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);

    // Cleanup
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [sendResponsesOnExit]);

  // Intercepter le bouton retour du navigateur
  useEffect(() => {
    // Ajouter une entrée dans l'historique pour pouvoir intercepter le retour
    window.history.pushState({ quizActive: true }, '', window.location.href);

    const handlePopState = () => {
      // L'utilisateur a cliqué sur le bouton retour du navigateur
      // Afficher le modal de confirmation
      setShowExitWarning(true);
      // Re-push l'état pour empêcher la navigation immédiate
      window.history.pushState({ quizActive: true }, '', window.location.href);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      // Le quiz se ferme (fin normale ou Quitter) : on retire l'entrée qu'on a
      // ajoutée, et seulement elle. Le listener est déjà retiré, le popstate
      // qui suit n'ouvre donc pas le modal. Avant, la sortie faisait go(-2) :
      // la seconde marche arrière quittait la page de l'élève et retombait sur
      // la page de connexion, ce qui ressemblait à une déconnexion.
      if (window.history.state?.quizActive) window.history.go(-1);
    };
  }, []);





  // On vérifie les réponses de l'élève avec les réponses officielles du test pour donner un score à chaque item pour notre élève !

  // Scoring — utilise compareAnswers (questionUtils) pour tous les types
  const verifScore = useCallback((answers) => {
    const newAnswers = { ...answers };
    Object.keys(answers).forEach((category) => {
      if (!answers[category]) return;
      Object.keys(answers[category]).forEach((questionKey) => {
        const entry = answers[category][questionKey];
        const studentResponse = entry.réponse || "";

        if (!studentResponse) {
          // Pas de réponse
          newAnswers[category][questionKey].score = "";
        } else if (isComplexAnswer(studentResponse) && isComplexAnswer(entry.bonne_reponse)) {
          // Tableau vrai/faux — comparaison spéciale existante
          newAnswers[category][questionKey].score = compareComplexAnswers(studentResponse, entry.bonne_reponse) ? 1 : 0;
        } else {
          // Tous les autres types — comparaison unifiée
          newAnswers[category][questionKey].score = compareAnswers(studentResponse, entry.bonne_reponse, entry.bonne_reponse_image, optionsDeFiche(entry)) ? 1 : 0;
        }
      });
    });
    setAnswers(newAnswers);
  }, []);



  /**
   * Remet la copie et ne poursuit QUE si le serveur a confirmé.
   *
   * Réessais : 3 pour une session QCM ou une éval nat, dont les endpoints
   * écrasent la ligne existante (`submit_responses`, `update_reponses`) et dont
   * l'XP est idempotente par `ref_id` — rejouer y est sans conséquence.
   * UN SEUL essai pour un défi : `soumettre_defi` crée une ligne neuve à chaque
   * appel, un rejeu mangerait un 2e slot du quota du jour ET créditerait l'XP
   * deux fois.
   *
   * Délai de 30 s par essai : la soumission la plus lente mesurée en charge est
   * à 21 s, et sans délai axios attend indéfiniment (aucun réessai ne partirait).
   *
   * En cas d'échec définitif, l'élève RESTE sur le quiz : la copie est gardée
   * sous la main pour un renvoi, et la fenêtre du beacon est rouverte.
   */
  const envoyerCopie = useCallback(async (results, apresSucces) => {
    setEnvoiEnCours(true);
    const envoi = await doPost(results, submitUrl, {
      tentatives: isDefi ? 1 : 3,
      timeoutMs: 30000,
    });
    setEnvoiEnCours(false);

    if (envoi?.success) {
      copieEnAttenteRef.current = null;
      setEnvoiEchoue(false);
      // Compteur local optimiste, seulement pour un défi TERMINÉ : un défi quitté
      // n'entre pas dans les cinq crédités du jour ; le profil rechargé fait foi.
      if (isDefi && results?.termine === true) marquerDefiFait();
      refreshProfil(); // l'XP est créditée par le back à la soumission → rafraîchir /me/
      apresSucces();
      return true;
    }

    copieEnAttenteRef.current = { results, apresSucces };
    dejaEnvoyeRef.current = false; // rouvre le filet sendBeacon si l'onglet se ferme
    setEnvoiEchoue(true);
    return false;
  }, [doPost, submitUrl, isDefi, refreshProfil]);

  const reessayerEnvoi = useCallback(async () => {
    const enAttente = copieEnAttenteRef.current;
    if (!enAttente) return;
    dejaEnvoyeRef.current = true; // referme la fenêtre du beacon le temps de l'essai
    await envoyerCopie(enAttente.results, enAttente.apresSucces);
  }, [envoyerCopie]);

  const postReponses = useCallback(async () => {
    dejaEnvoyeRef.current = true; // AVANT l'await : ferme la fenêtre du beacon
    verifScore(answers);
    stasherBilanQuiz(answers); // les scores viennent d'être posés par verifScore
    // Fin de quiz : `termine: true`, la seule copie qui rapporte de l'XP.
    const results = payloadCopie({
      termine: true, isDefi, isSession, evaluationId: evalBilan.id, eleveId, classeId: classe_id,
      reponses: isDefi ? undefined : annoterCopie(answers),
      defi: isDefi ? buildDefiPayload(answers) : undefined,
      rejeuDe: evalBilan.rejeuDe,
    });

    await envoyerCopie(results, () => setLaunch(false));
  }, [answers, annoterCopie, buildDefiPayload, stasherBilanQuiz, envoyerCopie, eleveId, evalBilan.id, evalBilan.rejeuDe, verifScore, setLaunch, classe_id, isSession, isDefi]);

  
  // Le Timer gère maintenant son propre état et appelle handleTimeUp quand le temps est écoulé
  const handleTimeUp = useCallback(() => {
    // Pas de setLaunch(false) ici : c'est postReponses qui sort du quiz, et
    // seulement si le serveur a confirmé la remise.
    postReponses();
  }, [postReponses]);

  // Confirmer la sortie du quiz (envoi des réponses vides pour les questions manquantes)
  const handleConfirmExit = useCallback(async () => {
    dejaEnvoyeRef.current = true; // AVANT l'await : ferme la fenêtre du beacon
    playAbandon();
    const completeAnswers = prepareAnswersWithEmptyResponses();

    // Calculer les scores
    Object.keys(completeAnswers).forEach(category => {
      Object.keys(completeAnswers[category]).forEach(questionKey => {
        const answer = completeAnswers[category][questionKey];
        if (answer.réponse && answer.réponse !== "") {
          const studentResponse = answer.réponse;
          const correctResponse = answer.bonne_reponse;

          if (isComplexAnswer(studentResponse) && isComplexAnswer(correctResponse)) {
            answer.score = compareComplexAnswers(studentResponse, correctResponse) ? 1 : 0;
          } else {
            // Comparateur unifié — même juge que verifScore (JSON, construction
            // géométrique, normalisation accents/casse). L'ancienne égalité
            // stricte comptait faux des réponses justes (constructions, JSON).
            answer.score = compareAnswers(studentResponse, correctResponse, answer.bonne_reponse_image, optionsDeFiche(answer)) ? 1 : 0;
          }
        }
      });
    });

    setAnswers(completeAnswers);
    stasherBilanQuiz(completeAnswers);

    // Bouton Quitter : la copie part, blancs compris, `termine: false` : pas d'XP.
    const results = payloadCopie({
      termine: false, isDefi, isSession, evaluationId: evalBilan.id, eleveId, classeId: classe_id,
      reponses: isDefi ? undefined : annoterCopie(completeAnswers),
      defi: isDefi ? buildDefiPayload(completeAnswers) : undefined,
      rejeuDe: evalBilan.rejeuDe,
    });

    // Fermé AVANT l'envoi : si celui-ci échoue, c'est la fenêtre de renvoi qui
    // doit s'afficher, pas deux modales empilées.
    setShowExitWarning(false);
    await envoyerCopie(results, () => {
      // Pas de navigation ici : fermer le quiz suffit, le nettoyage de l'effet
      // d'interception retire l'entrée d'historique ajoutée à l'ouverture.
      setLaunch(false);
    });
  }, [prepareAnswersWithEmptyResponses, annoterCopie, buildDefiPayload, stasherBilanQuiz, evalBilan.id, evalBilan.rejeuDe, eleveId, classe_id, envoyerCopie, setLaunch, isSession, isDefi]);


 
const handleNextQuestion = () => {
  setSlideDir('next');   // transition directionnelle (sortie gauche, entrée droite)
  setIsMounted(false);
  setImageLoading(true); // Reset le loader d'image
  setRevealed(false);    // referme le feedback Duolingo au changement de question
  setAideOuverte(false); // referme le coup de pouce
  setTimeout(() => {
     const currentCategoryIndex = categoriesOrder.indexOf(currentPart);
     const questionsInCurrentCategory = Object.keys(questions[currentPart] || {});
    // Vérifier si on est à la fin de la catégorie actuelle
      if (currentQuestionIndex >= questionsInCurrentCategory.length - 1) {
        // Vérifier s'il y a une catégorie suivante
        if (currentCategoryIndex < categoriesOrder.length - 1) {
          // Passer à la catégorie suivante
          setCurrentPart(categoriesOrder[currentCategoryIndex + 1]);
          setCurrentQuestionIndex(0);
        } else {
          // C'était la dernière question de la dernière catégorie
          postReponses();
        }
      } else {
        // Passer à la question suivante dans la même catégorie
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      }
      setHasAnswered(false);
      setIsMounted(true);
    }, 500);
  };



  // Gérer le retour à la question précédente
  const handlePrevQuestion = () => {
    setSlideDir('prev');   // transition directionnelle (sortie droite, entrée gauche)
    setIsMounted(false);
    setImageLoading(true); // Reset le loader d'image
    setRevealed(false);
    setAideOuverte(false);
    setTimeout(() => {
      if (currentQuestionIndex > 0) {
        // Revenir à la question précédente dans la même catégorie
        setCurrentQuestionIndex(prev => prev - 1);
      } else {
        // Si on est à la première question de la catégorie
        const currentCategoryIndex = categoriesOrder.indexOf(currentPart);
        if (currentCategoryIndex > 0) {
          // Revenir à la dernière question de la catégorie précédente
          const previousCategory = categoriesOrder[currentCategoryIndex - 1];
          const questionsInPreviousCategory = Object.keys(questions[previousCategory] || {});
          setCurrentPart(previousCategory);
          setCurrentQuestionIndex(questionsInPreviousCategory.length - 1);
        }
      }
      setIsMounted(true);
    }, 500);
  };


  
  // Mémoriser l'ID de la question courante pour stabiliser les callbacks
  const questionId = useMemo(() =>
    Object.keys(questions[currentPart] || {})[currentQuestionIndex],
    [questions, currentPart, currentQuestionIndex]
  );

  // Tip contextuel du bas — stable par question (jamais de tirage nu dans le JSX).
  const tipCourant = useMemo(() => {
    const ordre = categoriesOrder.length ? categoriesOrder : [currentPart];
    const comptes = ordre.map(p => Object.keys(questions[p] || {}).length);
    const total = comptes.reduce((a, b) => a + b, 0);
    const idx = Math.max(0, ordre.indexOf(currentPart));
    const courant = comptes.slice(0, idx).reduce((a, b) => a + b, 0) + currentQuestionIndex + 1;
    return pickTip(courant, total);
  }, [questions, categoriesOrder, currentPart, currentQuestionIndex]);

  const isAnswered = currentPart && questions[currentPart] && answers[currentPart] && answers[currentPart][questionId]?.réponse;

  // En-tête : simple fondu ; carte : slide directionnel (classes CSS scopées .eleve-gamified).
  const transitionClass = isMounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10";
  const cardTransitionClass = isMounted
    ? (slideDir === 'prev' ? 'quiz-in-left' : 'quiz-in-right')
    : (slideDir === 'prev' ? 'quiz-out-right' : 'quiz-out-left');
  const currentQuestion = currentPart && questions[currentPart] && questions[currentPart][questionId];

  // ─── questionContenu : sous-objet contenu pour l'AFFICHAGE uniquement ───
  // Extrait currentQuestion.contenu (choix, pairs, items, multi, etc.) pour QuestionInteractive.
  // BanqueQuestion → contenu existe déjà, lecture directe.
  // EvalNat → contenu n'existe pas, on le construit via buildPreviewContenu.
  // ⚠ NE PAS utiliser questionContenu.bonne_reponse pour le scoring — c'est une lettre ("D"),
  //   pas le contenu réel. Pour le scoring, utiliser currentQuestion.bonne_reponse.
  const questionContenu = useMemo(() => {
    if (!currentQuestion) return null;
    if (currentQuestion.contenu) return currentQuestion.contenu;
    const preview = buildPreviewContenu(currentQuestion);
    return preview?.contenu || null;
  }, [currentQuestion]);

  const questionType = currentQuestion?.type_question || currentQuestion?.type || 'qcm';



  // Mémoriser la config du tableau pour éviter les re-renders qui réinitialisent TableResponseElementEleve
  const tableConfig = useMemo(() => {
    if (!currentQuestion) return null;
    return getTableElementConfig(currentQuestion);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestion?.id, currentQuestion?.bonne_reponse]);

  // Stocker la réponse de l'élève — stabilisé avec useCallback pour éviter les re-renders
  // hasAnswered est piloté par onComplete depuis les composants interactifs
  const handleAnswer = useCallback((value, image) => {
    // bonne_reponse pour le scoring — toujours le contenu réel (pas la lettre)
    const bonneReponse = currentQuestion?.bonne_reponse || '';
    setAnswers(prev => ({
      ...prev,
      [currentPart]: {
        ...prev[currentPart],
        [questionId]: {
          réponse: value,
          bonne_reponse: bonneReponse,
          image,
          question_num: currentQuestion?.question_num,
          bonne_reponse_image: currentQuestion?.bonne_reponse_image || null,
          // Tolérance numérique (2,3 = 2,30) : décidée UNE FOIS depuis la
          // question (type + strict), embarquée dans la fiche → tous les
          // jugements (verifScore, beacon, verdict live, correction) suivent.
          tolerant: toleranceNumerique(currentQuestion),
          // Comparaison d'expression (2x - 8 = -8 + 2x) : flag et écritures admises
          // posés par le gen dans contenu, embarqués ici comme la tolérance ; et
          // la comparaison elle-même ('developpement' : le correcteur calcule).
          expression: comparaisonExpression(currentQuestion),
          acceptees: reponsesAcceptees(currentQuestion),
          comparaison: comparaisonDe(currentQuestion),
          // Groupes de trous sans ordre poses par le gen : embarques comme la tolerance,
          // pour que tous les jugements (score, correction, verdict) suivent la meme regle.
          interchangeables: trousInterchangeables(currentQuestion),
        }
      }
    }));
  }, [currentPart, questionId, currentQuestion]);

  // La correction à passer au widget (cf. `surPlace` plus bas) : sa réponse et
  // la bonne, dans l'encodage du widget. La lettre A-D devient la valeur du
  // choix par la fonction du scoring : le widget compare exactement ce qu'il a
  // lui-même posé. Calculée seulement une fois la réponse révélée.
  const corrigeCourant = useMemo(() => {
    if (!revealed) return null;
    const rec = answers?.[currentPart]?.[questionId] || {};
    return {
      bonneReponse: resolveBonneReponse({ ...questionContenu, bonne_reponse: rec.bonne_reponse ?? questionContenu?.bonne_reponse }),
      reponseEleve: rec.réponse ?? '',
      // les règles de jugement de la fiche (tolérance…), pour affiner trou par trou
      options: optionsDeFiche(rec),
    };
  }, [revealed, answers, currentPart, questionId, questionContenu]);

  // ─── Lire d'abord le texte (11/10/2026) ───
  // À l'entrée d'une partie qui a un texte (compréhension : septembre, variante ou forme parallèle), le livre
  // s'ouvre une fois par partie, comme le veut la consigne du ministère (lire le texte en entier avant de
  // répondre) ; l'icône du livre reste là pour y revenir. Une partie sans texte ne change pas. (Avant les
  // retours anticipés : règle des hooks.)
  const partiesLuesRef = useRef(new Set());
  const [lectureImposee, setLectureImposee] = useState(false);
  const texteALire = useMemo(() => partieAvecTexte(currentQuestion, currentPart), [currentQuestion, currentPart]);
  useEffect(() => {
    if (!texteALire || !currentPart || partiesLuesRef.current.has(currentPart)) return;
    partiesLuesRef.current.add(currentPart);
    setLectureImposee(true);
    setShowTextModal(true);
  }, [texteALire, currentPart]);
  const fermerTexte = useCallback(() => {
    setShowTextModal(false);
    setLectureImposee(false);
  }, []);


 if(!currentQuestion){
  return <Loading loadingText={"Chargement de la question..."} />
 }
  // Écran d'attente du premier envoi. Pendant un RENVOI (envoiEchoue), on ne
  // sort pas de la modale : c'est son propre bouton qui porte le sablier.
  if(envoiEnCours && !envoiEchoue){
    return <div> Chargement ... </div>
  }

  // Vérifier si c'est une catégorie avec texte de lecture
  // (les textes de compréhension, d'hier et d'aujourd'hui : evaluationConfig).
  // Une variante de compréhension a son propre texte (utils/supportLecture.js, generators/evalnat/supports.js) :
  // le livre ouvre ce texte-là, pas celui de septembre. Fonction pure, sans hook (zone après les retours anticipés).
  const supportVariante = supportDeQuestion(currentQuestion);
  const hasFrenchText = texteALire;

  // En-tête minimal — titre + prof (si dispo) + nombre de questions
  const totalQuestions = Object.keys(questions[currentPart] || {}).length;

  // Progression par grandes parties (affichage seulement — aucune logique de réponse touchée)
  const partsOrder = categoriesOrder.length ? categoriesOrder : [currentPart];
  const currentPartIndex = Math.max(0, partsOrder.indexOf(currentPart));
  const partCounts = partsOrder.map(p => Object.keys(questions[p] || {}).length);
  const globalTotal = partCounts.reduce((a, b) => a + b, 0);
  const globalCurrent = partCounts.slice(0, currentPartIndex).reduce((a, b) => a + b, 0) + currentQuestionIndex + 1;
  const hasMultipleParts = partsOrder.length > 1;

  // ─── Feedback Duolingo (mode correction visible uniquement) ───
  // Verdict + estimé d'XP de la question courante (cosmétique ; back autoritatif).
  const correctionVisible = !!evalBilan?.correction_visible;
  const currentRec = (answers[currentPart] && answers[currentPart][questionId]) || null;
  const reponseSaisie = currentRec?.réponse || '';
  const currentVerdict = reponseSaisie
    ? (isComplexAnswer(reponseSaisie) && isComplexAnswer(currentRec.bonne_reponse)
        ? compareComplexAnswers(reponseSaisie, currentRec.bonne_reponse)
        : compareAnswers(reponseSaisie, currentRec.bonne_reponse, currentRec.bonne_reponse_image, optionsDeFiche(currentRec)))
    : false;
  // Coup de pouce utilisé sur CETTE question → bonne réponse à XP réduite
  // (affichage seul ; le back appliquera la même règle au crédit réel).
  // Défi quotidien → barème AUTO 10/7/3 (sans le ×1,8 prof), même contrat que
  // le crédit réel du back (/defis-quotidiens/submit/).
  // Entraînement libre (défi) : coup de pouce visible SI l'exerciseur en a un
  // (getIndice résout par (attendu, generator_key) — les questions de défi
  // portent generator_key, cf. EspaceEleve.handleStartDefi). Séance en ligne :
  // le réglage de la passation, posé par le prof (R16) ; le flag démo ne
  // l'emporte plus. Bilan EvalNat : flag démo inchangé (OFF en prod).
  const cpVisible = isDefi ? !!getIndice(currentQuestion)
    : isSession ? evalBilan?.coups_de_pouce_autorises === true
      : cpEnabled;
  const aideUtiliseeIci = cpVisible && !!aidesUtilisees[`${currentPart}::${questionId}`];
  // Calculatrice : flag PÉDAGOGIQUE `contenu.calculatrice` posé par les
  // générateurs (639-646 true, fractions false) — s'applique partout où le
  // flag est vrai : défi libre comme session prof. Appel direct (pas de hook :
  // zone post-early-returns, même régime que getIndice ci-dessus).
  const calcVisible = calculatriceAutorisee(currentQuestion);
  const xpJuste = isDefi ? XP_BONNE_AUTO : XP_BONNE;
  const xpJusteAide = isDefi ? XP_BONNE_AIDE_AUTO : XP_BONNE_AIDE;
  const xpFaux = isDefi ? XP_EFFORT_AUTO : XP_MAUVAIS;
  const currentXp = currentVerdict ? (aideUtiliseeIci ? xpJusteAide : xpJuste) : xpFaux;

  // ─── Correction SUR PLACE (2026-09-21) ───
  // Révélé et faux : le widget qui est DÉJÀ à l'écran reçoit la correction et
  // marque ses propres éléments (juste / faux / attendu). Rien ne se redessine
  // ailleurs, et la feuille du bas se réduit à une barre de verdict qui ne
  // cache pas la question. Bonne réponse : rien ne change, la petite feuille
  // verte reste. Les douze formats sont marqués (lots 1 à 6). (`corrigeCourant`
  // est calculé plus haut, avant les retours de chargement : règle des hooks.)
  // Rejeu d'un quiz en ligne, correction masquée par le prof : une question
  // reprise à l'identique (`verdictSeul`) dit juste ou faux, sans la réponse attendue.
  const surPlace = correctionSurPlace(currentQuestion, { correctionVisible, revealed, verdict: currentVerdict });
  // Minuteur : affiché uniquement si une durée valide > 0 (0/absente/invalide → pas de chrono)
  const dureeMin = Number(evalBilan?.duree);
  const hasTimer = Number.isFinite(dureeMin) && dureeMin > 0;

  // Lecture audio (TTS navigateur) — frontend only
  const ttsSupported = typeof window !== 'undefined' && 'speechSynthesis' in window;
  const handleToggleSpeech = () => {
    if (!ttsSupported) return;
    const synth = window.speechSynthesis;
    if (synth.speaking) { synth.cancel(); setIsSpeaking(false); return; }
    const clean = (currentQuestion?.texte || '')
      .replace(/\{\{[^}]*\}\}/g, ' ')
      .replace(/\$[^$]*\$/g, ' ')
      .replace(/<[^>]*>/g, ' ')
      .replace(/&[a-z]+;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (!clean) return;
    const u = new SpeechSynthesisUtterance(clean);
    u.lang = 'fr-FR';
    u.onend = () => setIsSpeaking(false);
    u.onerror = () => setIsSpeaking(false);
    synth.cancel();
    synth.speak(u);
    setIsSpeaking(true);
  };

 // Ambiance « volcan » : un défi lancé depuis le cratère garde le fond
 // basalte/lave pendant la passation (immersion continue) — les quiz profs
 // et bilans restent sur le navy de l'île.
 const fondVolcan = evalBilan?.ambiance === 'volcan';

 return (
  <div
    className='eleve-gamified min-h-screen relative overflow-hidden'
    style={{
      background: fondVolcan
        ? 'radial-gradient(90% 70% at 50% 118%, rgba(255,90,31,.30), transparent 60%), radial-gradient(130% 100% at 50% 50%, #241428 0%, #150c1c 55%, #090610 100%)'
        : 'linear-gradient(180deg,#0A1F3D,#122E54 62%,#0A1F3D)',
    }}
  >
    {/* Fond immersif — halos discrets (cyan/orange en navy, braise en volcan) */}
    <div className="absolute inset-0 pointer-events-none">
      <div className="absolute -top-24 -left-16 w-96 h-96 rounded-full blur-3xl" style={{ background: fondVolcan ? 'rgba(255,106,31,.08)' : 'rgba(41,182,232,.10)' }} />
      <div className="absolute bottom-0 -right-16 w-80 h-80 rounded-full blur-3xl" style={{ background: fondVolcan ? 'rgba(255,90,31,.14)' : 'rgba(249,115,22,.08)' }} />
    </div>

    {/* Content — colonne app responsive */}
    <div className='relative z-10 flex flex-col items-center pt-2 pb-0 sm:py-4 px-0 sm:px-4 w-full max-w-xl sm:max-w-2xl mx-auto min-h-[100dvh]'>
      {/* En-tête MINIMAL façon Duolingo : ✕ · barre de progression · icônes.
          Le titre/prof et le nom de partie permanent ont disparu (immersion) —
          le changement de partie s'annonce par un toast éphémère, la série par
          un flash. La barre est l'unique repère permanent. */}
      <div className={`w-full mb-1 px-3 sm:px-1 transition-all duration-500 ease-out ${transitionClass}`}>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowExitWarning(true)}
            aria-label="Quitter le quiz"
            title="Quitter le quiz"
            className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-colors"
            style={{ background: 'rgba(255,255,255,.08)', color: 'rgba(255,255,255,.6)' }}
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <QuizProgress
              partsOrder={partsOrder}
              partCounts={partCounts}
              currentPartIndex={currentPartIndex}
              currentQuestionIndex={currentQuestionIndex}
              totalQuestions={totalQuestions}
              globalCurrent={globalCurrent}
              globalTotal={globalTotal}
              hasMultipleParts={hasMultipleParts}
            />
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {hasTimer && (
              <Timer duration={dureeMin * 60} onTimeUp={handleTimeUp} variant="compact" />
            )}
            {hasFrenchText && (
              <button
                type="button"
                onClick={() => setShowTextModal(true)}
                aria-label="Lire le texte"
                title="Lire le texte"
                className="w-9 h-9 rounded-full flex items-center justify-center text-white transition-colors"
                style={{ background: 'rgba(255,255,255,.1)' }}
              >
                <BookOpen className="w-5 h-5" />
              </button>
            )}
            {ttsSupported && (
              <button
                type="button"
                onClick={handleToggleSpeech}
                aria-label={isSpeaking ? 'Arrêter la lecture' : 'Écouter la question'}
                title={isSpeaking ? 'Arrêter la lecture' : 'Écouter la question'}
                className="w-9 h-9 rounded-full flex items-center justify-center text-white transition-colors"
                style={{ background: isSpeaking ? 'rgba(249,115,22,.9)' : 'rgba(255,255,255,.1)' }}
              >
                {isSpeaking ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </button>
            )}
            {/* Mute des effets sonores (bonne/mauvaise/level-up) */}
            <button
              type="button"
              onClick={toggleSfx}
              aria-label={sfxMuted ? 'Activer les sons' : 'Couper les sons'}
              title={sfxMuted ? 'Activer les sons' : 'Couper les sons'}
              className="w-9 h-9 rounded-full flex items-center justify-center text-white transition-colors"
              style={{ background: 'rgba(255,255,255,.1)' }}
            >
              {sfxMuted ? <BellOff className="w-5 h-5" /> : <Bell className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Toast éphémère de changement de partie (façon « nouvelle leçon ») */}
      {partToast && (
        <div
          className="fixed top-16 left-1/2 -translate-x-1/2 z-40 px-4 py-2 rounded-full text-sm font-extrabold whitespace-nowrap quiz-milestone-toast"
          style={{ fontFamily: 'Fredoka', background: 'rgba(255,185,56,.95)', color: '#3b2303', boxShadow: '0 8px 20px rgba(0,0,0,.35)' }}
        >
          {libellePartie(partToast)}{hasMultipleParts ? ` · ${currentPartIndex + 1}/${partsOrder.length}` : ''}
        </div>
      )}

      {/* Flash éphémère de série (le badge permanent a quitté l'en-tête) */}
      {correctionVisible && streakFlash && (
        <div className="fixed top-16 right-4 z-40 quiz-milestone-toast">
          <StreakBadge streak={streak} />
        </div>
      )}

      {/* Modal de confirmation de sortie */}
      {showExitWarning && (
        <div className='fixed inset-0 z-50 flex items-center justify-center'>
          {/* Overlay sombre */}
          <div
            className='absolute inset-0 bg-black/80 backdrop-blur-sm'
            onClick={() => setShowExitWarning(false)}
          />

          {/* Contenu du modal */}
          <div className='relative z-10 w-[90vw] max-w-md bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden'>
            {/* Header du modal */}
            <div className='bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-4 flex items-center gap-3'>
              <AlertTriangle className='w-6 h-6' />
              <h2 className='font-bold text-xl'>Quitter le quiz ?</h2>
            </div>

            {/* Contenu */}
            <div className='p-6'>
              <p className='text-slate-300 text-lg mb-4'>
                Tu n'as pas terminé toutes les questions !
              </p>
              <p className='text-slate-400 text-sm mb-6'>
                Si tu quittes maintenant, tes réponses sont envoyées, mais tu ne gagnes pas d'XP : l'XP, c'est quand tu vas au bout.
              </p>

              {/* Boutons */}
              <div className='flex gap-3'>
                <button
                  onClick={() => setShowExitWarning(false)}
                  className='flex-1 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-xl transition-colors'
                >
                  Continuer le quiz
                </button>
                <button
                  onClick={handleConfirmExit}
                  className='flex-1 px-4 py-3 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-medium rounded-xl transition-colors'
                >
                  Quitter
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Échec de remise de la copie.
          Ouverte sur l'échec REMONTÉ PAR L'ENVOI, jamais sur un compteur dérivé.
          Pas de bouton « fermer » ni de clic sur l'overlay : tant que la copie
          n'est pas partie, l'élève ne doit pas croire qu'elle l'est. */}
      {envoiEchoue && (
        <div className='fixed inset-0 z-[60] flex items-center justify-center'>
          <div className='absolute inset-0 bg-black/85 backdrop-blur-sm' />

          <div className='relative z-10 w-[90vw] max-w-md bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden'>
            <div className='bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-4 flex items-center gap-3'>
              <WifiOff className='w-6 h-6' />
              <h2 className='font-bold text-xl'>Ta copie n'est pas partie</h2>
            </div>

            <div className='p-6'>
              <p className='text-slate-300 text-lg mb-4'>
                Vérifie ta connexion, puis réessaie.
              </p>
              <p className='text-slate-400 text-sm mb-6'>
                Tes réponses sont conservées : ne ferme pas cette page.
              </p>

              <button
                onClick={reessayerEnvoi}
                disabled={envoiEnCours}
                className='w-full px-4 py-3 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2'
              >
                {envoiEnCours ? (
                  <>
                    <Loader2 className='w-5 h-5 animate-spin' />
                    Envoi en cours…
                  </>
                ) : (
                  <>
                    <RefreshCw className='w-5 h-5' />
                    Réessayer
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Le livre en mode lecture : ouvert par l'icône du livre, ou de lui-même à l'entrée d'une partie qui a un
          texte (lectureImposee : la consigne de lire d'abord, et « Page suivante » jusqu'à la dernière page) */}
      {showTextModal && hasFrenchText ? (
        <LivreImmersif
          key={supportVariante?.id || currentPart}
          titre={supportVariante?.titre}
          supportId={supportVariante?.id}
          typeLabel={currentPart}
          niveau={classeNom && classeNom[0]}
          pages={supportVariante?.pages}
          lectureImposee={lectureImposee}
          onFermer={fermerTexte}
        />
      ) : null}


      {/* Carte question BLANCHE (style mockup) — relative pour le bouton coup de pouce */}
      <div className={`w-full mt-2 sm:mt-4 flex-1 sm:flex-none bg-white rounded-t-2xl rounded-b-none sm:rounded-2xl shadow-xl shadow-black/20 p-4 sm:p-6 relative ${cardTransitionClass}`}>
        {/* Trace de provenance (demande prof 2026-07-28, campagnes de test) :
            filigrane visible UNIQUEMENT pour les instances issues d'un
            générateur — évals classiques et questions manuelles n'ont pas de
            generator_key, rien ne s'affiche pour elles. */}
        {currentQuestion.generator_key ? (
          <span className="absolute top-1.5 right-3 text-[9px] font-mono text-slate-300 select-none pointer-events-none">
            {currentQuestion.attendu ? `gen_${currentQuestion.attendu} · ` : ''}{currentQuestion.generator_key}
          </span>
        ) : null}
        <QuizThemeContext.Provider value="clair">
          {tableConfig ? (
            <div className='w-full'>
              {/* Question texte + éléments inline */}
              {currentQuestion.texte && (
                <div className="mb-4 text-base sm:text-lg">
                  <RichTextBlockEleve texte={currentQuestion.texte} elements={currentQuestion.elements_additionnels} theme="light" />
                </div>
              )}
              <TableResponseElementEleve
                config={tableConfig}
                light
                onAnswerChange={(formattedAnswer) => {
                  if (formattedAnswer && formattedAnswer !== "") {
                    handleAnswer(formattedAnswer, null);
                    setHasAnswered(true);
                  }
                }}
                reponseExistante={answers[currentPart]?.[Object.keys(questions[currentPart])[currentQuestionIndex]]?.réponse}
                setHasAnswered={setHasAnswered}
                corrige={surPlace ? corrigeCourant : null}
              />
            </div>
          ) : (
            <div className='space-y-4 w-full'>
              {/* Énoncé : image (zoomable) OU texte enrichi clair */}
              {currentQuestion.image ? (
                <div className='rounded-xl p-3 sm:p-4 border border-slate-200 bg-slate-50 relative'>
                  {imageLoading && (
                    <div className='flex items-center justify-center py-8'>
                      <Loader2 className='w-8 h-8 text-blue-500 animate-spin' />
                    </div>
                  )}
                  <div className={imageLoading ? 'opacity-0 absolute' : 'opacity-100'}>
                    <ZoomableMedia
                      type="image"
                      src={`${currentQuestion.image}`}
                      alt="Question"
                      className="w-fit mx-auto"
                      imgClassName="max-w-full max-h-[40vh] h-auto rounded-lg mx-auto"
                      onLoad={() => setImageLoading(false)}
                    />
                  </div>
                </div>
              ) : (
                <div className="text-lg sm:text-xl text-center" style={{ fontFamily: 'Fredoka', color: '#0A1F3D' }}>
                  <RichTextBlockEleve texte={currentQuestion.texte} elements={currentQuestion.elements_additionnels} theme="light" />
                </div>
              )}

              {/* Réponses — composant interactif unifié.
                  key = identité de la question : force un remontage complet à chaque
                  changement (reset state with a key) → pas de fuite d'état interne. */}
              <QuestionInteractive
                key={`${currentPart}-${currentQuestionIndex}`}
                typeQuestion={questionType}
                contenu={questionContenu}
                selectedValue={isAnswered}
                onAnswer={handleAnswer}
                onComplete={setHasAnswered}
                radioName={`response-${currentPart}-${currentQuestionIndex}`}
                tableConfig={tableConfig}
                onAbandon={handleNextQuestion}
                corrige={surPlace ? corrigeCourant : null}
                expression={comparaisonExpression(currentQuestion)}
              />
            </div>
          )}
        </QuizThemeContext.Provider>
      </div>

      {/* Coup de pouce : FAB Tano en bas à droite + bottom-sheet non bloquante
          — rendus en PORTAIL (hors carte, insensibles aux slides). Visible en
          ENTRAÎNEMENT LIBRE (défi) si l'exerciseur a une fiche cdp ; séance
          en ligne = réglage de la passation ; bilan = flag démo, cf. cpVisible.
          1er clic = comptabilisé (XP réduite) ; re-consultation libre ensuite. */}
      {cpVisible && (
        <CoupDePouceButton
          question={currentQuestion}
          xpPlein={xpJuste}
          xpAide={xpJusteAide}
          open={aideOuverte}
          used={!!aidesUtilisees[`${currentPart}::${questionId}`]}
          onOpen={() => {
            setAideOuverte(true);
            setAidesUtilisees(prev => ({ ...prev, [`${currentPart}::${questionId}`]: true }));
          }}
          onClose={() => setAideOuverte(false)}
        />
      )}

      {/* Calculatrice (bas-gauche, rangée À DROITE du FAB d'aide quand il
          est là) — SANS key : Ans persiste entre questions consécutives
          autorisées, comme une vraie calculatrice posée sur la table ; se
          réinitialise au démontage sur une question sans flag. */}
      {calcVisible && <CalculatriceCollege decale={cpVisible} cosinus={calculatriceCosinus(currentQuestion)} />}

      {/* Feedback Duolingo (mode correction visible) : verdict + bonne réponse + estimé XP */}
      {correctionVisible && revealed && (
        <XpAnswerFeedback
          ambiance={fondVolcan ? 'volcan' : 'navy'}
          verdict={currentVerdict}
          xp={currentXp}
          message={feedbackMsg}
          streakPalier={streakPalier}
          streakMessage={streakMsg}
          onContinue={handleNextQuestion}
        />
      )}

      {/* Navigation (sous la carte, sur le navy) */}
      <div className='w-full flex justify-between items-center gap-3 mt-3 mb-3 px-3 sm:px-0'>
        <div>
          {/* En mode correction_visible : pas de retour autorisé — la bonne réponse a déjà
              été révélée à l'élève sur chaque question précédente, revenir en arrière lui
              permettrait de modifier ses réponses après coup. */}
          {!correctionVisible && (currentQuestionIndex > 0 || categoriesOrder.indexOf(currentPart) > 0) && (
            <button
              className="flex items-center gap-1 px-5 py-3 bg-white text-slate-800 text-sm sm:text-base font-bold rounded-xl border-b-4 border-slate-300 active:translate-y-[2px] active:border-b-2 transition-all"
              onClick={handlePrevQuestion}
            >
              <ChevronLeft className='w-4 h-4 sm:w-5 sm:h-5' />
              <span>Préc.</span>
            </button>
          )}
        </div>
        <div>
          {hasAnswered && (() => {
            // Hors correction visible → « Suivant » direct (comportement historique).
            if (!correctionVisible) {
              return (
                <button
                  className="flex items-center gap-1 px-6 sm:px-8 py-3 text-white text-sm sm:text-base font-bold rounded-xl border-b-4 border-blue-700 active:translate-y-[2px] active:border-b-2 transition-all"
                  style={{ background: '#3B82F6' }}
                  onClick={handleNextQuestion}
                >
                  <span>Suivant</span>
                  <ChevronRight className='w-4 h-4 sm:w-5 sm:h-5' />
                </button>
              );
            }
            // Correction visible, pas encore vérifié → « Vérifier ».
            if (!revealed) {
              return (
                <button
                  className="flex items-center gap-1 px-6 sm:px-8 py-3 text-white text-sm sm:text-base font-bold rounded-xl border-b-4 active:translate-y-[2px] active:border-b-2 transition-all"
                  style={{ background: '#16a34a', borderBottomColor: '#15803d' }}
                  onClick={() => {
                    // Série de bonnes réponses (cosmétique) : incrément au SEUL clic
                    // « Vérifier » — le retour arrière est interdit dans ce mode,
                    // aucun double comptage possible.
                    const nouveauStreak = currentVerdict ? streak + 1 : 0;
                    bestStreakRef.current = Math.max(bestStreakRef.current, nouveauStreak);
                    setStreak(nouveauStreak);
                    const palier = currentVerdict && PALIERS_STREAK.includes(nouveauStreak) ? nouveauStreak : 0;
                    setStreakPalier(palier);
                    setStreakMsg(palier ? pickStreakMessage(palier) : null);
                    setFeedbackMsg(pickMessage(currentVerdict ? 'bonne' : 'mauvaise'));
                    setRevealed(true);
                    // Bonne comme mauvaise réponse : l'élève clique « Continuer »
                    // (plus de passage automatique — décision user 2026-08-10,
                    // le temps de savourer la fête vidéo)
                  }}
                >
                  <span>Vérifier</span>
                </button>
              );
            }
            // Révélé → le bottom-sheet (portail) gère la suite (auto si juste, « Continuer » si faux).
            return null;
          })()}
        </div>
      </div>

      {/* Correction sur place : la barre de verdict est fixée en bas, on laisse
          de la place pour que la dernière tuile marquée passe au-dessus. */}
      {surPlace ? <div className="h-36" aria-hidden="true" /> : null}

      {/* Tip contextuel en bas (mi-parcours / dernière ligne droite / conseils tournants) */}
      <div className="mt-6 text-center">
        <p className="text-xs sm:text-sm" style={{ color: 'rgba(255,255,255,.6)' }}>
          💡 {tipCourant}
        </p>
      </div>
    </div>
  </div>
);
};

